import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";

import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import {
  listProductos,
  createProducto,
  updateProducto,
  deleteProducto,
} from "../services/telusgyProductosService";

const EMPTY_FORM = {
  sku: "",
  name: "",
  category: "",
  brand: "",
  specsText: "{}",
  stockStatus: true,
};

const getError = (error) =>
  error?.response?.data?.error || error?.message || "No se pudo completar la operacion.";

export default function TelusgyProductosPage() {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [specsError, setSpecsError] = useState("");
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listProductos();
      setProductos(Array.isArray(data) ? data : []);
    } catch (error) {
      setToast({ open: true, severity: "error", message: getError(error) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSpecsError("");
    setOpen(true);
  };

  const openEdit = (product) => {
    setEditingId(product.id);
    setForm({
      sku: product.sku,
      name: product.name,
      category: product.category,
      brand: product.brand,
      specsText: JSON.stringify(product.specs ?? {}, null, 2),
      stockStatus: !!product.stockStatus,
    });
    setSpecsError("");
    setOpen(true);
  };

  const handleSpecsChange = (value) => {
    setForm((current) => ({ ...current, specsText: value }));
    try {
      JSON.parse(value || "{}");
      setSpecsError("");
    } catch {
      setSpecsError("El JSON de specs no es válido.");
    }
  };

  const handleSave = async () => {
    if (!form.sku.trim() || !form.name.trim() || !form.category.trim() || !form.brand.trim()) {
      setToast({ open: true, severity: "warning", message: "SKU, nombre, categoría y marca son obligatorios." });
      return;
    }
    let specs;
    try {
      specs = JSON.parse(form.specsText || "{}");
    } catch {
      setToast({ open: true, severity: "warning", message: "El JSON de specs no es válido." });
      return;
    }

    setSaving(true);
    try {
      const body = {
        sku: form.sku.trim(),
        name: form.name.trim(),
        category: form.category.trim(),
        brand: form.brand.trim(),
        specs,
        stockStatus: form.stockStatus,
      };
      if (editingId) {
        await updateProducto(editingId, body);
        setToast({ open: true, severity: "success", message: "Producto actualizado." });
      } else {
        await createProducto(body);
        setToast({ open: true, severity: "success", message: "Producto creado." });
      }
      setOpen(false);
      load();
    } catch (error) {
      setToast({ open: true, severity: "error", message: getError(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try {
      await deleteProducto(id);
      setToast({ open: true, severity: "success", message: "Producto eliminado." });
      load();
    } catch (error) {
      setToast({ open: true, severity: "error", message: getError(error) });
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Productos Telusgy"
        subtitle="Catálogo de productos administrado desde el ERP"
        backTo="/dashboard"
        actions={(
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton onClick={load} disabled={loading}>
              <RefreshIcon />
            </IconButton>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: "none" }}>
              Nuevo producto
            </Button>
          </Box>
        )}
      />

      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Categoría</TableCell>
                  <TableCell>Marca</TableCell>
                  <TableCell>Stock</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && !productos.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No hay productos registrados todavía.
                    </TableCell>
                  </TableRow>
                )}
                {productos.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.sku}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{p.category}</TableCell>
                    <TableCell>{p.brand}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={p.stockStatus ? "Disponible" : "Agotado"}
                        color={p.stockStatus ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(p)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        {!loading && !!productos.length && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {productos.length} producto(s) en total.
          </Typography>
        )}
      </Box>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? "Editar producto" : "Nuevo producto"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 1.25, pt: 0.5 }}>
            <TextField
              size="small"
              label="SKU *"
              value={form.sku}
              onChange={(e) => setForm((c) => ({ ...c, sku: e.target.value }))}
            />
            <TextField
              size="small"
              label="Nombre *"
              value={form.name}
              onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))}
            />
            <TextField
              size="small"
              label="Categoría *"
              value={form.category}
              onChange={(e) => setForm((c) => ({ ...c, category: e.target.value }))}
            />
            <TextField
              size="small"
              label="Marca *"
              value={form.brand}
              onChange={(e) => setForm((c) => ({ ...c, brand: e.target.value }))}
            />
            <TextField
              size="small"
              multiline
              minRows={4}
              label="Specs (JSON)"
              value={form.specsText}
              onChange={(e) => handleSpecsChange(e.target.value)}
              error={!!specsError}
              helperText={specsError || 'Ej: { "potencia": "450W", "eficiencia": "98.7%" }'}
              sx={{ fontFamily: "monospace" }}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.stockStatus}
                  onChange={(e) => setForm((c) => ({ ...c, stockStatus: e.target.checked }))}
                />
              )}
              label="Disponible en stock"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <LoadingButton variant="contained" loading={saving} onClick={handleSave} disabled={!!specsError}>
            Guardar
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((current) => ({ ...current, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
